import { Spinner } from '../common/Spinner';
import { useInvestmentSummary } from '../../hooks/useProjects';
import { formatCurrency, formatHours, formatPercent } from '../../utils/format';

interface Props {
  projectId: number;
}

export default function InvestmentSummarySection({ projectId }: Props) {
  const { data: summary, isLoading, error } = useInvestmentSummary(projectId);

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Investment Summary</h2>
        <div className="flex justify-center py-8">
          <Spinner />
        </div>
      </div>
    );
  }

  if (error || !summary) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Investment Summary</h2>
        <p className="text-sm text-red-600">Failed to load investment summary</p>
      </div>
    );
  }

  if (summary.investors.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Investment Summary</h2>
        <p className="text-sm text-gray-500">No investment data available for this project.</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <h2 className="text-lg font-medium text-gray-900 mb-4">Investment Summary</h2>
      <div className="overflow-x-auto" tabIndex={0} role="region" aria-label="Investment summary table">
        <table className="min-w-full divide-y divide-gray-200">
          <thead>
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Investor
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Hours
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Investment Rate
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Total Investment
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Share %
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {summary.investors.map((investor) => (
              <tr key={investor.investorId ?? 'unknown'}>
                <td className="px-4 py-2 text-sm text-gray-900">{investor.investorName}</td>
                <td className="px-4 py-2 text-sm text-gray-900 text-right font-mono">
                  {formatHours(investor.hours)}
                </td>
                <td className="px-4 py-2 text-sm text-gray-900 text-right font-mono">
                  {investor.investmentRate != null ? formatCurrency(investor.investmentRate) : '-'}
                </td>
                <td className="px-4 py-2 text-sm text-gray-900 text-right font-mono">
                  {formatCurrency(investor.totalInvestment)}
                </td>
                <td className="px-4 py-2 text-sm text-gray-900 text-right font-mono">
                  {formatPercent(investor.sharePercent)}
                </td>
              </tr>
            ))}
            {/* Grand Total row */}
            <tr className="bg-gray-50 font-medium">
              <td className="px-4 py-2 text-sm text-gray-900">Grand Total</td>
              <td className="px-4 py-2 text-sm text-gray-900 text-right font-mono">
                {formatHours(summary.grandTotal.hours)}
              </td>
              <td className="px-4 py-2 text-sm text-gray-900 text-right">-</td>
              <td className="px-4 py-2 text-sm text-gray-900 text-right font-mono">
                {formatCurrency(summary.grandTotal.totalInvestment)}
              </td>
              <td className="px-4 py-2 text-sm text-gray-900 text-right font-mono">100.0%</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}
